import { Search, MessageSquareText, QrCode, Link, Star } from 'lucide-react';
import React, { useCallback, useEffect, useState } from "react";
import DataTablePagination from '../DataTablePagination';
import debounce from 'lodash.debounce';
import Spacing from '../Spacing';
import MessageModal from '../Modal/MessageModal';
import { loadMessageTemplate } from "../../services/message";
import { can_visit, generatePatientTableData, getPractice, handelCopyLink, handleQRScanPage } from "../../helpers";
import { useDocumentTitle } from '../../hooks/customDocumentTitleHook';
import { fetchPatients } from '../../services/patient';
import PatientDataTableForReview from '../DataTable/PatientDataTableForReview';

export default function Review({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(false); 
  const [buttonLoader, setButtonLoader] = useState(false);
  // const [messageTemplate] = useState(userMessageTemplates());
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedValue, setSelectedValue] = useState(2);
  const [filter, setFilter] = useState({
    account_id: account_id,
    location_id: location_id,
    page: 1,
    per_page: 10,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    sex: "",
    mobile_phone: "",
    start_age: "",
    end_age: "",
    insurance_ids: [],
    last_visit: "",
    visit_day_query_type: "",
    visited_last_days: "",
    not_visited_last_days: "",
    risk_level: "",
    preventative_due: "",
    order_by: [],
    city: "",
    state: "",
    zip: "",
    patient_type: selectedValue,
    message_type: "Review",
  });

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [messageTemplate, setMessageTemplate] = useState([]);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplate = async () => {
    const response = await loadMessageTemplate(account_id, 'review');
    if (response) {
      setMessageTemplate(response.data.results);
    }
  };

  useEffect(() => {
    loadData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchPatients(getFilter);
      if (response) {
        const generateData = generatePatientTableData(response?.data?.results);
        setData(generateData);
        setDataCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
        if (checkedIds.length < 1) {
          setSelectAll(false);
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadReviewData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilter((prevFilter) => ({ ...prevFilter, search_term: value, page: 1 }));
    }, 600), // 600ms debounce delay
    []
  );
  
  const handleChangeSearchTerm = (event) => {
    const value = event.target.value;
    setDataLoading(true);
    debouncedSearch(value);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };


  useEffect(() => {    
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setCheckedIds([]);
    setDataLoading(true);
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleFilterCheckboxChange = (value) => {
    setSelectedValue(value);
    setDataLoading(true);
    setFilter((prevFilter) => ({
      ...prevFilter,
      patient_type: value,
      page: 1,
    }));
  };

  const checkboxes = [
    { value: 1, label: "All", tooltip: "All Patients" },
    { value: 2, label: "Active", tooltip: "Active Patients Only" },
    { value: 3, label: "Inactive", tooltip: "Inactive Patients Only" },
    { value: 4, label: "Self Pay", tooltip: "Self Pay Patients Only" },
  ];

  useDocumentTitle("Review");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_teal_bg docpro_white_color">
            <Star width={20} height={20} />
          </i>
          Get Reviews
          {/* <span>{numberWithCommas(dataCount.totalCount)}</span> */}
        </h2>
        {can_visit("_single_message") && (
          <div className="docpro_page_heading_btns">
            <button
              type="button"
              className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn docpro_sm_btn"
              onClick={() => {setActiveModal(!activeModal); setPreviousScrollPosition(window.scrollY); window.scrollTo(0, 0);}}
            >
              <MessageSquareText width={20} height={20} />
              <span className="text">Get review for new patients</span>
            </button>
          </div>
        )}
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings docpro_grid_gap_10 small_heading">
          <div className="searching_div">
            <form className="docpro_search_form width_100">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>

            <div className="filter_type">
              {checkboxes.map((checkbox) => (
                <div
                  key={checkbox.value}
                  className="docpro_checkbox docpro_tip_checkbox"
                  style={{ marginLeft: checkbox.value === 1 ? 0 : "12px" }}
                >
                  <input
                    type="checkbox"
                    name="patient_type"
                    value={checkbox.value}
                    checked={selectedValue === checkbox.value}
                    onChange={() => handleFilterCheckboxChange(checkbox.value)}
                  />
                  <label style={{ marginLeft: "5px" }}>{checkbox.label}</label>
                  <span className="docpro_tip">{checkbox.tooltip}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="docpro_data_table_btns p-0">
            {getPractice("google_map_place_id") !== null &&
              getPractice("google_map_place_id") && (
                <>
                  <button
                    className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                    onClick={() => handleQRScanPage("review")}
                  >
                    <QrCode width={20} height={20} />
                    Review QR code
                  </button>
                  <button
                    className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                    onClick={() =>
                      handelCopyLink(
                        `https://search.google.com/local/writereview?placeid=${getPractice(
                          "google_map_place_id"
                        )}`
                      )
                    }
                  >
                    <Link width={20} height={20} />
                    Review link
                  </button>
                </>
              )}
            {/* <Filter filterType="advance" /> */}
          </div>
        </div>
        <PatientDataTableForReview
          tableData={data}
          handleSelectAll={() => handleSelectAll(data)}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          reloadData={loadData}
          reloadDataFilter={filter}
          messageTemplate={messageTemplate}
          user_id={user_id}
          account_id={account_id}
          location_id={location_id}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          buttonLoader={buttonLoader}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPatientData={selectedPatientData}
          setSelectedPatientData={setSelectedPatientData}
        />
        {data.length > 0 && !dataLoading && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {activeModal && (
        <MessageModal
          last_name={""}
          first_name={""}
          phone={""}
          message_type="Review"
          messageTemplate={messageTemplate}
          account_id={account_id}
          location_id={location_id}
          activeModal={activeModal}
          setActiveModal={setActiveModal}
          setSmsLimit={setSmsLimit}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}